import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { Plus, Trash2, Upload } from "react-feather";
import { apiErrorMessage, useGetLazy, usePost } from "../../api";
import { usePermissions } from "../../hooks";
import {
  IBlacklistedDomain,
  IBlacklistedDomainsAddPreview,
  IBlacklistedDomainsAddResponse,
  IBlacklistedDomainsRemovePreview,
  IBlacklistedDomainsRemoveResponse,
  IBlacklistedDomainsResponse,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";
import {
  EmptyState,
  FilterBar,
  FilterField,
  PageHeader,
  PageToolbar,
  ToolbarButton,
} from "../../components/ui";

const BASE = "blacklisted-domains";
const PAGE_SIZE = 50;
/** The API rejects more ids than this in one request. */
const MAX_SELECTION = 5000;
const SELECTION_LIMIT_MESSAGE = `You can select at most ${MAX_SELECTION} domains at a time. Remove these first, then select more.`;

// Whitespace separates entries, and bare domains may also be split on commas or semicolons.
// A URL token is kept whole because its path can contain them; the server rejects those as ambiguous.
const parseDomains = (text: string): string[] =>
  text
    .split(/\s+/)
    .flatMap((token) => (/[/?#]/.test(token) ? [token] : token.split(/[,;]/)))
    .map((s) => s.trim())
    .filter(Boolean);

export const BlacklistedDomains: React.FC = () => {
  const { can, isAdmin } = usePermissions();
  const canAdd =
    isAdmin ||
    can(PermissionAction.CreateAny, PermissionResource.BlacklistedDomain);
  const canRemove =
    isAdmin ||
    can(PermissionAction.DeleteAny, PermissionResource.BlacklistedDomain);

  const [searchInput, setSearchInput] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<IBlacklistedDomainsResponse | null>(null);
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<IBlacklistedDomain[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { execute: fetchList, loading, error } =
    useGetLazy<IBlacklistedDomainsResponse>();

  const latestRequest = useRef(0);

  const load = useCallback(
    async (search: string, p: number) => {
      const requestId = ++latestRequest.current;
      const params = new URLSearchParams({
        page: String(p),
        pageSize: String(PAGE_SIZE),
      });
      if (search) params.set("search", search);
      const r = await fetchList(`${BASE}?${params}`).catch(() => null);
      // A newer request has started; this answer is stale and must not overwrite it.
      if (!r || requestId !== latestRequest.current) return;
      // Removals can empty the page we're on; fall back to the last page that has rows.
      if (r.rows.length === 0 && p > 1) {
        setPage(Math.max(1, r.pagination.totalPages));
        return;
      }
      setData(r);
    },
    [fetchList]
  );

  useEffect(() => {
    load(applied, page);
  }, [load, applied, page]);

  const applySearch = (next: string) => {
    setSelected(new Map());
    if (next === applied && page === 1) {
      load(next, 1);
      return;
    }
    setPage(1);
    setApplied(next);
  };

  const search = () => applySearch(searchInput.trim());

  const toggle = (row: IBlacklistedDomain, on: boolean) => {
    if (on && !selected.has(row.id) && selected.size >= MAX_SELECTION) {
      setNotice(SELECTION_LIMIT_MESSAGE);
      return;
    }
    setSelected((prev) => {
      const next = new Map(prev);
      if (on) next.set(row.id, row.domain);
      else next.delete(row.id);
      return next;
    });
  };

  const rows = data?.rows ?? [];
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = (on: boolean) => {
    const next = new Map(selected);
    let capped = false;
    for (const r of rows) {
      if (!on) {
        next.delete(r.id);
      } else if (!next.has(r.id)) {
        if (next.size >= MAX_SELECTION) {
          capped = true;
          break;
        }
        next.set(r.id, r.domain);
      }
    }
    setSelected(next);
    if (capped) setNotice(SELECTION_LIMIT_MESSAGE);
  };

  const selectedRows = (): IBlacklistedDomain[] =>
    Array.from(selected, ([id, domain]) => ({ id, domain }));

  const pagination = data?.pagination;

  return (
    <div>
      <PageHeader
        title="Blacklisted Domains"
        description="Domains held in the campaigns database's BlacklistedDomains table. Every add and remove is recorded in the change audit."
        actions={
          canAdd && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus size={14} className="me-1" />
              Add domains
            </Button>
          )
        }
      />

      <FilterBar
        onSearch={search}
        onClear={() => {
          setSearchInput("");
          applySearch("");
        }}
        activeCount={applied ? 1 : 0}
        searchLabel="Search"
        summary={
          loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Searching…
            </>
          ) : pagination ? (
            `${pagination.total} blacklisted domain${pagination.total === 1 ? "" : "s"}${applied ? ` matching "${applied}"` : ""}`
          ) : undefined
        }
      >
        <FilterField label="Domain" hint="Matches anywhere in the domain." md={6}>
          <Form.Control
            size="sm"
            placeholder="example.com"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
        </FilterField>
      </FilterBar>

      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {notice && (
        <Alert variant="info" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {canRemove && (
        <PageToolbar>
          <ToolbarButton
            label="Remove selected"
            hint="Removes every ticked domain, including ones ticked on other pages. Up to 5000 at a time."
            variant="outline-danger"
            count={selected.size}
            disabled={selected.size === 0}
            onClick={() => setRemoving(selectedRows())}
          />
          <ToolbarButton
            label="Clear selection"
            hint="Untick every domain."
            disabled={selected.size === 0}
            onClick={() => setSelected(new Map())}
          />
        </PageToolbar>
      )}

      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {loading && data && (
        <div className="text-center text-muted small py-2">
          <Spinner animation="border" size="sm" className="me-1" />
          Searching…
        </div>
      )}

      {data && rows.length === 0 && (
        <EmptyState
          title={applied ? "No domains match" : "Nothing is blacklisted"}
          message={
            applied
              ? "Try a shorter search."
              : "Added domains appear here."
          }
        />
      )}

      {rows.length > 0 && (
        <>
          <Table hover size="sm" responsive>
            <thead>
              <tr>
                {canRemove && (
                  <th style={{ width: 36 }}>
                    <Form.Check
                      checked={allOnPage}
                      onChange={(e) => toggleAll(e.target.checked)}
                      aria-label="Select all on this page"
                    />
                  </th>
                )}
                <th>Domain</th>
                {canRemove && <th style={{ width: 60 }} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {canRemove && (
                    <td>
                      <Form.Check
                        checked={selected.has(row.id)}
                        onChange={(e) => toggle(row, e.target.checked)}
                        aria-label={`Select ${row.domain}`}
                      />
                    </td>
                  )}
                  <td>{row.domain}</td>
                  {canRemove && (
                    <td className="text-end">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        title={`Remove ${row.domain}`}
                        onClick={() => setRemoving([row])}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>

          {pagination && pagination.totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-between">
              <span className="text-muted small">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <ButtonGroup size="sm">
                <Button
                  variant="outline-secondary"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={page >= pagination.totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </ButtonGroup>
            </div>
          )}
        </>
      )}

      {adding && (
        <AddModal
          onClose={() => setAdding(false)}
          onChanged={(message) => {
            setNotice(message);
            load(applied, page);
          }}
        />
      )}
      {removing && (
        <RemoveModal
          rows={removing}
          onClose={() => setRemoving(null)}
          onRemoved={(res) => {
            setSelected((prev) => {
              const next = new Map(prev);
              for (const r of res.removed) next.delete(r.id);
              for (const id of res.notFound) next.delete(id);
              return next;
            });
            setNotice(
              `Removed ${res.removed.length} domain${res.removed.length === 1 ? "" : "s"}` +
                (res.notFound.length
                  ? `; ${res.notFound.length} had already been removed.`
                  : ".")
            );
            load(applied, page);
          }}
        />
      )}
    </div>
  );
};

// ── Add ──────────────────────────────────────────────────────────────────────

const AddModal: React.FC<{
  onClose: () => void;
  onChanged: (message: string) => void;
}> = ({ onClose, onChanged }) => {
  const [text, setText] = useState("");
  const [comment, setComment] = useState("");
  const [result, setResult] = useState<IBlacklistedDomainsAddResponse | null>(
    null
  );
  const [preview, setPreview] = useState<IBlacklistedDomainsAddPreview | null>(
    null
  );
  const [failure, setFailure] = useState<string | null>(null);
  const { execute: post, loading } = usePost<IBlacklistedDomainsAddResponse>();
  const { execute: postPreview, loading: previewing } =
    usePost<IBlacklistedDomainsAddPreview>();

  const entries = parseDomains(text);

  const readFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      setText((prev) => `${prev ? `${prev}\n` : ""}${String(reader.result ?? "")}`);
    reader.readAsText(file);
  };

  const runPreview = async () => {
    setFailure(null);
    try {
      setPreview(await postPreview(`${BASE}/preview`, { domains: entries }));
    } catch (e) {
      setFailure(apiErrorMessage(e));
    }
  };

  const submit = async () => {
    if (!preview) return;
    setFailure(null);
    try {
      // Only what the preview showed as new is sent, so the confirmed list is what gets added.
      const res = await post(BASE, { domains: preview.toAdd, comment });
      const skipped = [
        ...res.skipped,
        ...preview.alreadyPresent,
        ...preview.repeated,
      ];
      setResult({ ...res, skipped, invalid: preview.invalid });
      onChanged(
        `Added ${res.added.length} domain${res.added.length === 1 ? "" : "s"}` +
          (skipped.length ? `; ${skipped.length} already present or repeated.` : ".")
      );
    } catch (e) {
      setFailure(apiErrorMessage(e));
    }
  };

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add blacklisted domains</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <>
            <Alert variant={result.added.length ? "success" : "warning"}>
              Added {result.added.length}, skipped {result.skipped.length}{" "}
              (already blacklisted or repeated), invalid {result.invalid.length}.
            </Alert>
            {result.invalid.length > 0 && (
              <>
                <div className="fw-semibold small mb-1">Not added: invalid entries</div>
                <ul className="small">
                  {result.invalid.slice(0, 50).map((i) => (
                    <li key={i.row}>
                      #{i.row}: {i.input} ({i.reason})
                    </li>
                  ))}
                  {result.invalid.length > 50 && (
                    <li>…and {result.invalid.length - 50} more</li>
                  )}
                </ul>
              </>
            )}
          </>
        ) : preview ? (
          <>
            <Alert variant={preview.toAdd.length ? "info" : "warning"}>
              {preview.toAdd.length} will be added. Nothing is saved until you confirm.
            </Alert>
            <PreviewList title="Will be added" items={preview.toAdd} variant="success" />
            <PreviewList
              title="Already blacklisted (skipped)"
              items={preview.alreadyPresent}
              variant="secondary"
            />
            <PreviewList
              title="Repeated in your list (skipped)"
              items={preview.repeated}
              variant="secondary"
            />
            <PreviewList
              title="Invalid entries (skipped)"
              items={preview.invalid.map((i) => `#${i.row}: ${i.input} (${i.reason})`)}
              variant="danger"
            />
            <Form.Group className="mt-3">
              <Form.Label className="small fw-semibold">
                Comment (optional)
              </Form.Label>
              <Form.Control
                size="sm"
                maxLength={500}
                placeholder="Why these are being blacklisted"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Form.Text muted>Saved with each audit entry.</Form.Text>
            </Form.Group>
            {failure && (
              <Alert variant="danger" className="mt-3 mb-0">
                {failure}
              </Alert>
            )}
          </>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Domains</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                placeholder={"example.com\nanother-example.co.uk"}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <Form.Text muted>
                One per line, or separated by commas. URLs are reduced to their host;
                www. and a leading *. are kept as typed. Up to 5000 entries. Put each
                URL on its own line: a comma or semicolon inside a URL can't be told
                apart from a separator, so those entries are rejected.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">
                <Upload size={14} className="me-1" />
                Or load a CSV / text file
              </Form.Label>
              <Form.Control
                type="file"
                size="sm"
                accept=".csv,.txt"
                onChange={(e) =>
                  readFile((e.target as HTMLInputElement).files?.[0])
                }
              />
              <Form.Text muted>
                A .csv or .txt with one domain per line in a single column, no header
                row and no quotes, for example:
              </Form.Text>
              <pre className="small bg-light border rounded p-2 mt-1 mb-1">
                {"example.com\nwww.another-example.co.uk\n*.third-example.com"}
              </pre>
              <Form.Text muted>
                Every cell is read as a domain, so extra columns or a header row are
                listed as invalid in the preview. The file's contents are added to the
                box above, where you can edit them before previewing.
              </Form.Text>
            </Form.Group>
            {failure && (
              <Alert variant="danger" className="mt-3 mb-0">
                {failure}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <Button onClick={onClose}>Done</Button>
        ) : preview ? (
          <>
            <Button
              variant="outline-secondary"
              onClick={() => setPreview(null)}
              disabled={loading}
            >
              Back
            </Button>
            <Button onClick={submit} disabled={preview.toAdd.length === 0 || loading}>
              {loading && <Spinner animation="border" size="sm" className="me-1" />}
              Add {preview.toAdd.length} domain{preview.toAdd.length === 1 ? "" : "s"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose} disabled={previewing}>
              Cancel
            </Button>
            <Button onClick={runPreview} disabled={entries.length === 0 || previewing}>
              {previewing && <Spinner animation="border" size="sm" className="me-1" />}
              Preview {entries.length || ""} entr{entries.length === 1 ? "y" : "ies"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// ── Remove ───────────────────────────────────────────────────────────────────

const PreviewList: React.FC<{
  title: string;
  items: string[];
  variant: string;
}> = ({ title, items, variant }) => {
  if (items.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="fw-semibold small">
        {title} <Badge bg={variant}>{items.length}</Badge>
      </div>
      <ul className="small mb-0" style={{ maxHeight: 160, overflowY: "auto" }}>
        {items.map((item, i) => (
          <li key={`${item}-${i}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const RemoveModal: React.FC<{
  rows: IBlacklistedDomain[];
  onClose: () => void;
  onRemoved: (res: IBlacklistedDomainsRemoveResponse) => void;
}> = ({ rows, onClose, onRemoved }) => {
  const [comment, setComment] = useState("");
  const [preview, setPreview] = useState<IBlacklistedDomainsRemovePreview | null>(
    null
  );
  const [failure, setFailure] = useState<string | null>(null);
  const [result, setResult] = useState<IBlacklistedDomainsRemoveResponse | null>(
    null
  );
  const { execute: post, loading } = usePost<IBlacklistedDomainsRemoveResponse>();
  const { execute: postPreview } = usePost<IBlacklistedDomainsRemovePreview>();

  useEffect(() => {
    postPreview(`${BASE}/remove/preview`, { ids: rows.map((r) => r.id) })
      .then(setPreview)
      .catch((e) => setFailure(apiErrorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!preview) return;
    setFailure(null);
    try {
      // Only rows the preview found are sent, so the confirmed list is what gets removed.
      const res = await post(`${BASE}/remove`, {
        ids: preview.toRemove.map((r) => r.id),
        comment,
      });
      const merged = { ...res, notFound: [...res.notFound, ...preview.notFound] };
      setResult(merged);
      onRemoved(merged);
    } catch (e) {
      setFailure(apiErrorMessage(e));
    }
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          Remove {rows.length} domain{rows.length === 1 ? "" : "s"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!preview && !failure && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {result && (
          <>
            <Alert variant={result.removed.length ? "success" : "warning"}>
              Removed {result.removed.length}, already removed {result.notFound.length}.
              The removal is recorded in the change audit.
            </Alert>
            <PreviewList
              title="Removed"
              items={result.removed.map((r) => r.domain)}
              variant="success"
            />
          </>
        )}
        {preview && !result && (
          <>
            <Alert variant={preview.toRemove.length ? "info" : "warning"}>
              {preview.toRemove.length} will be removed. Nothing is deleted until you confirm.
            </Alert>
            <PreviewList
              title="Will be removed"
              items={preview.toRemove.map((r) => r.domain)}
              variant="danger"
            />
            <PreviewList
              title="Already removed (skipped)"
              items={rows
                .filter((r) => preview.notFound.includes(r.id))
                .map((r) => r.domain)}
              variant="secondary"
            />
            <Form.Group className="mt-3">
              <Form.Label className="small fw-semibold">Comment (optional)</Form.Label>
              <Form.Control
                size="sm"
                maxLength={500}
                placeholder="Why these are being removed"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Form.Text muted>Saved with each audit entry.</Form.Text>
            </Form.Group>
          </>
        )}
        {failure && (
          <Alert variant="danger" className="mt-3 mb-0">
            {failure}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={submit}
              disabled={!preview || preview.toRemove.length === 0 || loading}
            >
              {loading && <Spinner animation="border" size="sm" className="me-1" />}
              Remove {preview ? preview.toRemove.length : ""}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
